import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import tokens from "./workspaceTokens";

const traceDraw = keyframes`
  0% {
    stroke-dashoffset: 104;
    opacity: 0.32;
  }
  48%, 78% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 0.42;
  }
`;

const scanAcross = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-24px);
  }
  18% { opacity: 0.72; }
  82% { opacity: 0.72; }
  100% {
    opacity: 0;
    transform: translateX(108px);
  }
`;

const endpointPulse = keyframes`
  0%, 45% {
    opacity: 0.5;
    transform: scale(0.72);
  }
  68% {
    opacity: 1;
    transform: scale(1.08);
  }
  100% {
    opacity: 0.62;
    transform: scale(0.82);
  }
`;

const WorkspaceChartLoadingState = ({ title, body }) => (
  <Box
    role="status"
    aria-live="polite"
    sx={{
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.25,
      px: 3,
      py: 4,
      textAlign: "center",
    }}
  >
    <Box
      data-workspace-chart-loading-indicator
      aria-hidden="true"
      sx={{
        position: "relative",
        width: 96,
        height: 60,
        overflow: "hidden",
        borderRadius: `${tokens.radii.md}px`,
        border: `1px solid ${tokens.accentBorder}`,
        background: `linear-gradient(155deg, ${tokens.accentSoft}, ${tokens.panelSoft} 68%)`,
        boxShadow: `${tokens.innerHighlight}, 0 12px 30px rgba(0, 0, 0, 0.18)`,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 96 60"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <Box
          component="path"
          d="M12 14.5H84M12 30H84M12 45.5H84M24 9V51M48 9V51M72 9V51"
          fill="none"
          stroke={tokens.border}
          strokeWidth="1"
        />
        <path
          d="M10 43L23 35L35 38L48 25L59 30L72 18L86 12"
          fill="none"
          stroke={tokens.accentBorder}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <Box
          component="path"
          data-workspace-chart-loading-trace
          d="M10 43L23 35L35 38L48 25L59 30L72 18L86 12"
          fill="none"
          stroke={tokens.accentHover}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="104"
          strokeDasharray="104"
          strokeDashoffset="104"
          sx={{
            animation: `${traceDraw} 1.9s cubic-bezier(0.42, 0, 0.2, 1) infinite`,
            filter: `drop-shadow(0 0 4px ${tokens.accent})`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              strokeDashoffset: 0,
              opacity: 1,
            },
          }}
        />
        <Box
          component="circle"
          data-workspace-chart-loading-endpoint
          cx="86"
          cy="12"
          r="3.2"
          fill={tokens.accentHover}
          sx={{
            transformBox: "fill-box",
            transformOrigin: "center",
            animation: `${endpointPulse} 1.9s ease-out infinite`,
            filter: `drop-shadow(0 0 5px ${tokens.accent})`,
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              opacity: 1,
              transform: "scale(1)",
            },
          }}
        />
      </Box>
      <Box
        data-workspace-chart-loading-sweep
        sx={{
          position: "absolute",
          top: 7,
          bottom: 7,
          left: 0,
          width: 22,
          borderRight: `1px solid ${tokens.accentHover}`,
          background: `linear-gradient(90deg, transparent, ${tokens.accentSoft})`,
          animation: `${scanAcross} 1.9s cubic-bezier(0.42, 0, 0.2, 1) infinite`,
          "@media (prefers-reduced-motion: reduce)": {
            display: "none",
          },
        }}
      />
    </Box>
    <Typography
      data-workspace-chart-loading-title
      sx={{
        color: tokens.text,
        fontSize: tokens.sizes.body,
        fontWeight: tokens.weights.semibold,
      }}
    >
      {title}
    </Typography>
    {body ? (
      <Typography
        sx={{
          maxWidth: 320,
          color: tokens.muted,
          fontSize: tokens.sizes.secondary,
          lineHeight: 1.5,
        }}
      >
        {body}
      </Typography>
    ) : null}
  </Box>
);

export default WorkspaceChartLoadingState;
